import { motion } from "framer-motion";
import { Waves, Hotel, KeyRound, ArrowUpRight, Hammer } from "lucide-react";
import { Link } from "react-router-dom";
import { scrollToId } from "@/lib/content";
import { useTexts, useExtras } from "@/lib/texts";
import { photoUrl } from "@/components/Portfolio";

const EASE = [0.16, 1, 0.3, 1];
const LAYOUT = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4"];
const CARD_ICONS = [Waves, Hotel, KeyRound, Hammer];

const ImageCard = ({ s, i, span }) => (
    <motion.article
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
        data-testid={`bento-card-${i}`}
        className={`group relative overflow-hidden ${span} min-h-[320px]`}
    >
        <img
            src={photoUrl(s.photo)}
            alt={s.title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/90 via-[#1C1917]/30 to-transparent" />
        <div className="relative flex h-full flex-col justify-end p-7">
            <span className="mb-3 font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#E75C15]">
                {String(i + 1).padStart(2, "0")}
            </span>
            <h3 className="font-display text-2xl font-medium text-stone-100 sm:text-3xl">{s.title}</h3>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-stone-300">{s.desc}</p>
            <button
                onClick={() => scrollToId("contatti")}
                data-testid={`bento-cta-${i}`}
                className="mt-5 inline-flex w-fit items-center gap-2 border-b border-[#E75C15]/60 pb-1 text-xs font-medium uppercase tracking-wider text-[#E75C15] transition-colors hover:text-stone-100"
            >
                Richiedi un preventivo
                <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
        </div>
    </motion.article>
);

const SolidCard = ({ s, i, span }) => {
    const Icon = CARD_ICONS[i % CARD_ICONS.length];
    return (
        <motion.article
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
            data-testid={`bento-card-${i}`}
            className={`group spotlight-card flex min-h-[280px] flex-col justify-between border border-stone-900/10 bg-white p-7 transition-colors duration-500 hover:border-[#E75C15]/50 ${span}`}
        >
            <span className="flex h-12 w-12 items-center justify-center border border-[#E75C15]/40 text-[#C24E0F] transition-colors duration-500 group-hover:bg-[#1C1917] group-hover:text-[#E75C15]">
                <Icon className="h-5 w-5" />
            </span>
            <div className="relative z-[2]">
                <h3 className="font-display text-2xl font-medium text-stone-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-stone-600">{s.desc}</p>
                <button
                    onClick={() => scrollToId("contatti")}
                    data-testid={`bento-cta-${i}`}
                    className="mt-5 inline-flex items-center gap-2 border-b border-[#B8430D]/50 pb-1 text-xs font-medium uppercase tracking-wider text-[#B8430D] transition-colors hover:text-stone-900"
                >
                    Richiedi un preventivo
                    <ArrowUpRight className="h-3.5 w-3.5" />
                </button>
            </div>
        </motion.article>
    );
};

export const Services = () => {
    const texts = useTexts();
    const { serviceCards } = useExtras();

    return (
    <section id="servizi" data-testid="services-section" className="bg-[#F9F7F2] py-20 sm:py-28 lg:py-36">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
            <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
                <div>
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, ease: EASE }}
                        className="font-mono-alt text-xs uppercase tracking-[0.3em] text-[#C24E0F]"
                    >
                        Servizi
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                        className="mt-4 max-w-2xl font-display text-3xl font-light tracking-tight text-stone-900 sm:text-4xl lg:text-5xl"
                    >
                        {texts.services_heading}
                    </motion.h2>
                </div>
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
                    className="max-w-sm"
                >
                    <p className="text-sm leading-relaxed text-stone-600">
                        {texts.services_subtitle}
                    </p>
                    <Link
                        to="/servizi"
                        data-testid="services-detail-link"
                        className="group mt-4 inline-flex items-center gap-2 border-b border-[#B8430D]/50 pb-1 text-xs font-medium uppercase tracking-wider text-[#B8430D] transition-colors hover:text-stone-900"
                    >
                        Scopri i servizi nel dettaglio
                        <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-45" />
                    </Link>
                </motion.div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
                {serviceCards.map((s, i) => {
                    const span = LAYOUT[i % LAYOUT.length];
                    return s.photo ? (
                        <ImageCard key={i} s={s} i={i} span={span} />
                    ) : (
                        <SolidCard key={i} s={s} i={i} span={span} />
                    );
                })}
            </div>
        </div>
    </section>
    );
};
